/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator } from "@azure/core-paging";
import { SimplePollerLike, OperationState } from "@azure/core-lro";
import {
  EventSubscription,
  SystemTopicEventSubscriptionsListBySystemTopicOptionalParams,
  SystemTopicEventSubscriptionsGetOptionalParams,
  SystemTopicEventSubscriptionsGetResponse,
  SystemTopicEventSubscriptionsCreateOrUpdateOptionalParams,
  SystemTopicEventSubscriptionsCreateOrUpdateResponse,
  SystemTopicEventSubscriptionsDeleteOptionalParams,
  EventSubscriptionUpdateParameters,
  SystemTopicEventSubscriptionsUpdateOptionalParams,
  SystemTopicEventSubscriptionsUpdateResponse,
  SystemTopicEventSubscriptionsGetFullUrlOptionalParams,
  SystemTopicEventSubscriptionsGetFullUrlResponse,
  SystemTopicEventSubscriptionsGetDeliveryAttributesOptionalParams,
  SystemTopicEventSubscriptionsGetDeliveryAttributesResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a SystemTopicEventSubscriptions. */
export interface SystemTopicEventSubscriptions {
  /**
   * List event subscriptions that belong to a specific system topic.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param options The options parameters.
   */
  listBySystemTopic(
    resourceGroupName: string,
    systemTopicName: string,
    options?: SystemTopicEventSubscriptionsListBySystemTopicOptionalParams,
  ): PagedAsyncIterableIterator<EventSubscription>;
  /**
   * Get an event subscription.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription to be found.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    options?: SystemTopicEventSubscriptionsGetOptionalParams,
  ): Promise<SystemTopicEventSubscriptionsGetResponse>;
  /**
   * Asynchronously creates or updates an event subscription with the specified parameters. Existing
   * event subscriptions will be updated with this API.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription to be created. Event subscription names
   *                              must be between 3 and 64 characters in length and use alphanumeric letters only.
   * @param eventSubscriptionInfo Event subscription properties containing the destination and filter
   *                              information.
   * @param options The options parameters.
   */
  beginCreateOrUpdate(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    eventSubscriptionInfo: EventSubscription,
    options?: SystemTopicEventSubscriptionsCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SystemTopicEventSubscriptionsCreateOrUpdateResponse>,
      SystemTopicEventSubscriptionsCreateOrUpdateResponse
    >
  >;
  /**
   * Asynchronously creates or updates an event subscription with the specified parameters. Existing
   * event subscriptions will be updated with this API.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription to be created. Event subscription names
   *                              must be between 3 and 64 characters in length and use alphanumeric letters only.
   * @param eventSubscriptionInfo Event subscription properties containing the destination and filter
   *                              information.
   * @param options The options parameters.
   */
  beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    eventSubscriptionInfo: EventSubscription,
    options?: SystemTopicEventSubscriptionsCreateOrUpdateOptionalParams,
  ): Promise<SystemTopicEventSubscriptionsCreateOrUpdateResponse>;
  /**
   * Delete an existing event subscription of a system topic.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription to be deleted.
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    options?: SystemTopicEventSubscriptionsDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>>;
  /**
   * Delete an existing event subscription of a system topic.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription to be deleted.
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    options?: SystemTopicEventSubscriptionsDeleteOptionalParams,
  ): Promise<void>;
  /**
   * Update an existing event subscription of a system topic.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription to be updated.
   * @param eventSubscriptionUpdateParameters Updated event subscription information.
   * @param options The options parameters.
   */
  beginUpdate(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    eventSubscriptionUpdateParameters: EventSubscriptionUpdateParameters,
    options?: SystemTopicEventSubscriptionsUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SystemTopicEventSubscriptionsUpdateResponse>,
      SystemTopicEventSubscriptionsUpdateResponse
    >
  >;
  /**
   * Update an existing event subscription of a system topic.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription to be updated.
   * @param eventSubscriptionUpdateParameters Updated event subscription information.
   * @param options The options parameters.
   */
  beginUpdateAndWait(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    eventSubscriptionUpdateParameters: EventSubscriptionUpdateParameters,
    options?: SystemTopicEventSubscriptionsUpdateOptionalParams,
  ): Promise<SystemTopicEventSubscriptionsUpdateResponse>;
  /**
   * Get the full endpoint URL for an event subscription of a system topic.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription.
   * @param options The options parameters.
   */
  getFullUrl(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    options?: SystemTopicEventSubscriptionsGetFullUrlOptionalParams,
  ): Promise<SystemTopicEventSubscriptionsGetFullUrlResponse>;
  /**
   * Get all delivery attributes for an event subscription.
   * @param resourceGroupName The name of the resource group within the user's subscription.
   * @param systemTopicName Name of the system topic.
   * @param eventSubscriptionName Name of the event subscription.
   * @param options The options parameters.
   */
  getDeliveryAttributes(
    resourceGroupName: string,
    systemTopicName: string,
    eventSubscriptionName: string,
    options?: SystemTopicEventSubscriptionsGetDeliveryAttributesOptionalParams,
  ): Promise<SystemTopicEventSubscriptionsGetDeliveryAttributesResponse>;
}
